/**
 * Vúmetro profesional: onda temporal + espectro espejado con gradiente y peak hold.
 */
import React, { useEffect, useRef } from 'react'

const BAR_COUNT = 36

/**
 * @param {{
 *   stream: MediaStream|null,
 *   active?: boolean,
 *   paused?: boolean,
 *   height?: number,
 *   variant?: 'full'|'mini',
 * }} props
 */
function VoiceMeter({
  stream,
  active = false,
  paused = false,
  height = 72,
  variant = 'full',
}) {
  const canvasRef = useRef(null)
  const rafRef = useRef(0)
  const audioRef = useRef(null)
  const peaksRef = useRef([])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined

    let cancelled = false
    const barN = variant === 'mini' ? 16 : BAR_COUNT

    const cleanupAudio = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = 0
      }
      const pack = audioRef.current
      audioRef.current = null
      if (pack) {
        try {
          pack.source.disconnect()
        } catch (e) {
          /* ignore */
        }
        try {
          pack.analyser.disconnect()
        } catch (e) {
          /* ignore */
        }
        try {
          if (pack.ctx.state !== 'closed') pack.ctx.close()
        } catch (e) {
          /* ignore */
        }
      }
    }

    const paintIdle = () => {
      const ctx2d = canvas.getContext('2d')
      if (!ctx2d) return
      const dpr = window.devicePixelRatio || 1
      const w = canvas.clientWidth || 280
      const h = height
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx2d.clearRect(0, 0, w, h)

      const g = ctx2d.createLinearGradient(0, 0, w, 0)
      g.addColorStop(0, 'rgba(76,175,120,0.12)')
      g.addColorStop(0.5, 'rgba(255,82,82,0.1)')
      g.addColorStop(1, 'rgba(76,175,120,0.12)')
      ctx2d.fillStyle = g
      ctx2d.fillRect(0, 0, w, h)

      ctx2d.strokeStyle = 'rgba(255,255,255,0.08)'
      ctx2d.beginPath()
      ctx2d.moveTo(0, h * 0.5)
      ctx2d.lineTo(w, h * 0.5)
      ctx2d.stroke()

      const mid = h * 0.5
      ctx2d.fillStyle = 'rgba(255,255,255,0.12)'
      for (let i = 0; i < barN; i++) {
        const x = ((i + 0.5) / barN) * w
        const bh = 3 + (i % 5)
        ctx2d.fillRect(x - 1, mid - bh, 2, bh * 2)
      }
    }

    if (!stream || !active) {
      cleanupAudio()
      paintIdle()
      return cleanupAudio
    }

    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) {
      paintIdle()
      return cleanupAudio
    }

    const ctx = new AudioCtx()
    const analyser = ctx.createAnalyser()
    analyser.fftSize = variant === 'mini' ? 256 : 1024
    analyser.smoothingTimeConstant = 0.82
    const source = ctx.createMediaStreamSource(stream)
    source.connect(analyser)
    audioRef.current = { ctx, analyser, source }

    const freq = new Uint8Array(analyser.frequencyBinCount)
    const time = new Uint8Array(analyser.fftSize)
    if (!peaksRef.current.length || peaksRef.current.length !== barN) {
      peaksRef.current = new Array(barN).fill(0)
    }

    const draw = () => {
      if (cancelled) return
      rafRef.current = requestAnimationFrame(draw)
      const canvasEl = canvasRef.current
      if (!canvasEl) return
      const ctx2d = canvasEl.getContext('2d')
      if (!ctx2d) return

      const dpr = window.devicePixelRatio || 1
      const w = canvasEl.clientWidth || 280
      const h = height
      if (
        canvasEl.width !== Math.floor(w * dpr) ||
        canvasEl.height !== Math.floor(h * dpr)
      ) {
        canvasEl.width = Math.floor(w * dpr)
        canvasEl.height = Math.floor(h * dpr)
        ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0)
      }

      analyser.getByteFrequencyData(freq)
      analyser.getByteTimeDomainData(time)

      ctx2d.clearRect(0, 0, w, h)

      // fondo
      const bg = ctx2d.createLinearGradient(0, 0, 0, h)
      bg.addColorStop(0, 'rgba(0,0,0,0.45)')
      bg.addColorStop(1, 'rgba(20,28,24,0.55)')
      ctx2d.fillStyle = bg
      ctx2d.fillRect(0, 0, w, h)

      const mid = h * 0.55
      const specH = Math.max(8, h * 0.42)
      const gap = variant === 'mini' ? 2 : 2.5
      const rawBarW = (w - gap * (barN - 1)) / barN
      const barW = Math.max(1, rawBarW)
      const step = Math.max(1, Math.floor(freq.length / (barN * 1.35)))
      const peaks = peaksRef.current
      const damp = paused ? 0.12 : 1

      // espectro espejado
      for (let i = 0; i < barN; i++) {
        let sum = 0
        const start = i * step
        for (let j = 0; j < step && start + j < freq.length; j++) {
          sum += freq[start + j]
        }
        const avg = (sum / step / 255) * damp
        const eased = Math.pow(Math.max(0, avg), 0.85)
        peaks[i] = Math.max(eased, (peaks[i] || 0) * 0.92)

        const bh = Math.max(2, eased * specH)
        const x = i * (barW + gap)
        const grad = ctx2d.createLinearGradient(0, mid - bh, 0, mid + bh)
        if (paused) {
          grad.addColorStop(0, 'rgba(160,160,160,0.55)')
          grad.addColorStop(1, 'rgba(120,120,120,0.25)')
        } else if (eased > 0.72) {
          grad.addColorStop(0, 'rgba(255,120,90,0.95)')
          grad.addColorStop(0.5, 'rgba(255,82,82,0.75)')
          grad.addColorStop(1, 'rgba(255,60,60,0.2)')
        } else {
          grad.addColorStop(0, 'rgba(120,230,170,0.95)')
          grad.addColorStop(0.45, 'rgba(56,200,140,0.7)')
          grad.addColorStop(1, 'rgba(40,160,110,0.15)')
        }
        ctx2d.fillStyle = grad
        const radius = Math.max(0, Math.min(2, barW / 2))
        roundRect(ctx2d, x, mid - bh, barW, bh * 2, radius)
        ctx2d.fill()

        // peak hold
        const peakY = mid - peaks[i] * specH
        ctx2d.fillStyle = paused
          ? 'rgba(200,200,200,0.35)'
          : 'rgba(255,255,255,0.55)'
        ctx2d.fillRect(x, peakY, barW, 1.5)
      }

      // onda temporal (capa superior) — full y mini (HUD flotante)
      {
        ctx2d.beginPath()
        const slice = w / time.length
        const amp = h * (variant === 'mini' ? 0.32 : 0.28)
        for (let i = 0; i < time.length; i++) {
          const v = ((time[i] - 128) / 128) * damp
          const y = mid + v * amp
          if (i === 0) ctx2d.moveTo(0, y)
          else ctx2d.lineTo(i * slice, y)
        }
        ctx2d.strokeStyle = paused
          ? 'rgba(220,220,220,0.4)'
          : 'rgba(255,255,255,0.65)'
        ctx2d.lineWidth = variant === 'mini' ? 1 : 1.25
        ctx2d.stroke()

        ctx2d.save()
        ctx2d.globalCompositeOperation = 'lighter'
        ctx2d.strokeStyle = paused
          ? 'rgba(180,180,180,0.1)'
          : 'rgba(100,255,180,0.14)'
        ctx2d.lineWidth = variant === 'mini' ? 2.5 : 4
        ctx2d.stroke()
        ctx2d.restore()
      }

      // línea central
      ctx2d.strokeStyle = 'rgba(255,255,255,0.1)'
      ctx2d.beginPath()
      ctx2d.moveTo(0, mid)
      ctx2d.lineTo(w, mid)
      ctx2d.stroke()

      if (paused) {
        ctx2d.fillStyle = 'rgba(0,0,0,0.25)'
        ctx2d.fillRect(0, 0, w, h)
        ctx2d.fillStyle = 'rgba(255,255,255,0.55)'
        ctx2d.font = '600 11px system-ui,sans-serif'
        ctx2d.textAlign = 'center'
        ctx2d.fillText('PAUSA', w / 2, h / 2 + 4)
      }
    }

    void ctx.resume().catch(() => {})
    draw()

    return () => {
      cancelled = true
      cleanupAudio()
    }
  }, [stream, active, paused, height, variant])

  return (
    <canvas
      ref={canvasRef}
      className={`udv-voice-meter${variant === 'mini' ? ' is-mini' : ''}`}
      style={{ width: '100%', height, display: 'block' }}
      aria-hidden
    />
  )
}

function roundRect(ctx, x, y, w, h, r) {
  if (!(w > 0) || !(h > 0)) return
  const rr = Math.max(0, Math.min(Number(r) || 0, w / 2, h / 2))
  if (rr <= 0) {
    ctx.beginPath()
    ctx.rect(x, y, w, h)
    return
  }
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

export default VoiceMeter
